import { EvidenceEnforcement, EVIDENCE_ENFORCEMENT_LABELS } from '../tasks/evidence.enforcement';

/**
 * The tri-state TaskEvidenceOptions cannot express. That interface already carries both axes, but
 * at different resolutions: `<kind>Enabled` is per bucket while `mandatory` is per task, so
 * "before required, during optional" has nowhere to live. This is that missing resolution.
 * Named for SOR because jms-model already has an unrelated Photo* family for field images.
 */
export enum SorEvidencePolicy {
    Off = 'Off',
    Optional = 'Optional',
    RequiredExceptionAllowed = 'RequiredExceptionAllowed',
    // Stored value kept for existing data; it means Required (no exceptions).
    Required = 'Required',
}

export type SorEvidenceProfile = 'pending' | 'authorised';

/**
 * Kind and phase are flattened into the key, matching TaskEvidenceOptions' own vocabulary
 * (`beforePhotoEnabled`, `signatureEnabled`). A future non-photo requirement is one more key
 * rather than a parallel family or a nesting level, and signature — which has no phase — fits
 * without contorting the shape.
 *
 * On a catalogue code an absent key means "inherit the tenant default", which is how an override
 * is cleared. On an item every key in the profile is present, resolved at creation.
 */
export interface SorEvidenceOptions {
    beforePhoto?: SorEvidencePolicy;
    duringPhoto?: SorEvidencePolicy;
    afterPhoto?: SorEvidencePolicy;
}

/** Both profiles are frozen onto every SOR line, since a pending line may record work already done under the authorised set. */
export interface SorEvidenceSettings {
    pending: SorEvidenceOptions;
    authorised: SorEvidenceOptions;
}

/** Read when a tenant has never saved the setting, so resolution can never degrade to Off by accident. */
export const SOR_EVIDENCE_DEFAULTS: SorEvidenceSettings = {
    pending: { beforePhoto: SorEvidencePolicy.RequiredExceptionAllowed },
    authorised: { beforePhoto: SorEvidencePolicy.RequiredExceptionAllowed, duringPhoto: SorEvidencePolicy.Optional, afterPhoto: SorEvidencePolicy.RequiredExceptionAllowed },
};

const PROFILE_KEYS: Record<SorEvidenceProfile, (keyof SorEvidenceOptions)[]> = {
    pending: ['beforePhoto'],
    authorised: ['beforePhoto', 'duringPhoto', 'afterPhoto'],
};

/**
 * Produces the dense options frozen onto an item at creation, so a later change to the catalogue
 * or the tenant default cannot alter what a technician is already required to capture.
 * Keys outside the profile are omitted rather than set to Off, keeping the pending profile free of
 * during/after keys that every downstream reader would otherwise have to ignore.
 */
export function resolveSorEvidence(profile: SorEvidenceProfile, settings?: SorEvidenceSettings, override?: SorEvidenceSettings): SorEvidenceOptions {

    const profileDefault = settings?.[profile] ?? SOR_EVIDENCE_DEFAULTS[profile];
    const resolved: SorEvidenceOptions = {};

    for (const key of PROFILE_KEYS[profile]) {
        resolved[key] = override?.[profile]?.[key] ?? profileDefault?.[key] ?? SOR_EVIDENCE_DEFAULTS[profile]?.[key] ?? SorEvidencePolicy.Off;
    }

    return resolved;
}

/** The line snapshot: a pending line's authorised before is its pending before, so approval never changes what the request was photographed against (Fayez, 2026-07-29). */
export function resolveSorEvidenceSnapshot(isPending: boolean, settings?: SorEvidenceSettings, override?: SorEvidenceSettings): SorEvidenceSettings {

    const pending = resolveSorEvidence('pending', settings, override);
    const authorised = resolveSorEvidence('authorised', settings, override);

    return { pending, authorised: isPending ? { ...authorised, beforePhoto: pending.beforePhoto } : authorised };
}

/** The buckets approval stamps onto a pending task; before is left as the pending line was requested with. */
export const SOR_PROMOTED_BUCKETS: readonly SorEvidenceBucket[] = PROFILE_KEYS.authorised.filter(key => !PROFILE_KEYS.pending.includes(key));

/** One owner for the policy to level mapping, so a new policy value fails to compile here instead of slipping past a predicate. */
const SOR_POLICY_ENFORCEMENT: Record<SorEvidencePolicy, EvidenceEnforcement | undefined> = {
    [SorEvidencePolicy.Off]: undefined,
    [SorEvidencePolicy.Optional]: EvidenceEnforcement.Optional,
    [SorEvidencePolicy.RequiredExceptionAllowed]: EvidenceEnforcement.RequiredExceptionAllowed,
    [SorEvidencePolicy.Required]: EvidenceEnforcement.RequiredNoExceptions,
};

/** Off has no enforcement because the bucket is not enabled at all. */
export function sorPolicyToEnforcement(policy?: SorEvidencePolicy): EvidenceEnforcement | undefined {

    return policy ? SOR_POLICY_ENFORCEMENT[policy] : undefined;
}

/** Maps onto the TaskEvidenceOptions `<kind>Enabled` flag: Off is the only policy that hides the bucket. */
export function isSorEvidenceEnabled(policy?: SorEvidencePolicy): boolean {

    return sorPolicyToEnforcement(policy) !== undefined;
}

/** Separated from isSorEvidenceEnabled because an Optional bucket is shown but never blocks. */
export function isSorEvidenceRequired(policy?: SorEvidencePolicy): boolean {

    return (sorPolicyToEnforcement(policy) ?? EvidenceEnforcement.Optional) !== EvidenceEnforcement.Optional;
}

/** Borrows the enforcement wording so a SOR line and its task never label the same level differently. */
export const SOR_EVIDENCE_POLICY_LABELS: Record<SorEvidencePolicy, string> = {
    [SorEvidencePolicy.Off]: 'Off',
    [SorEvidencePolicy.Optional]: EVIDENCE_ENFORCEMENT_LABELS[EvidenceEnforcement.Optional],
    [SorEvidencePolicy.RequiredExceptionAllowed]: EVIDENCE_ENFORCEMENT_LABELS[EvidenceEnforcement.RequiredExceptionAllowed],
    [SorEvidencePolicy.Required]: EVIDENCE_ENFORCEMENT_LABELS[EvidenceEnforcement.RequiredNoExceptions],
};

/** Task-title wording without brackets, so a clipped closed picker still tells the two Required levels apart. */
export const SOR_EVIDENCE_POLICY_SHORT_LABELS: Record<SorEvidencePolicy, string> = {
    [SorEvidencePolicy.Off]: 'Off',
    [SorEvidencePolicy.Optional]: 'Optional',
    [SorEvidencePolicy.RequiredExceptionAllowed]: 'Required, exceptions allowed',
    [SorEvidencePolicy.Required]: 'Required',
};

/**
 * One bucket, named the way the policy names it. The task fields that hold the captured photos are
 * the plural `<phase>Photos` on the item's main task — a different vocabulary for the same three
 * phases, so the two are bridged once here rather than at every call site.
 */
export type SorEvidenceBucket = keyof SorEvidenceOptions;

export const SOR_EVIDENCE_BUCKETS: readonly SorEvidenceBucket[] = ['beforePhoto', 'duringPhoto', 'afterPhoto'];

/** Bucket → the `TaskEvidenceResponses` array it writes into. A rename on either side breaks here first. */
export const SOR_EVIDENCE_TASK_FIELDS: Record<SorEvidenceBucket, 'beforePhotos' | 'duringPhotos' | 'afterPhotos'> = {
    beforePhoto: 'beforePhotos',
    duringPhoto: 'duringPhotos',
    afterPhoto: 'afterPhotos',
};

export const SOR_EVIDENCE_BUCKET_LABELS: Record<SorEvidenceBucket, string> = {
    beforePhoto: 'Before',
    duringPhoto: 'During',
    afterPhoto: 'After',
};
